import type { MRT_Cell, MRT_ColumnDef } from 'mantine-react-table';

import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../../../../../shared/components/UI/Button/Button';
import { TenderTitle } from '../../../../../../../shared/components/UI/TenderTitle/TenderTitle';

import { formatDateInParisTZ } from '../../../../../../../shared/utils/dates';
import type { ProjectTableData } from './tableConfig';

export enum TableColumn {
  TITLE = 'title',
  RESPONSE_DEADLINE = 'responseDeadline',
  ACTION = 'action',
}

const fixedWidthProps = (width?: number) => ({
  size: width,
  maxSize: width,
  minSize: width,
  mantineTableBodyCellProps: {
    sx: { flex: `0 0 ${width}px`, flexGrow: 0 },
  },
  mantineTableHeadCellProps: {
    sx: { flex: `0 0 ${width}px`, flexGrow: 0 },
  },
});

type I18nTFunction = TFunction<'bidResponse'>;
type ColumnType = (t: I18nTFunction) => MRT_ColumnDef<ProjectTableData>;

export const bidResponseTitle: ColumnType = t => ({
  header: t('project.projectTable.cells.title.header'),
  id: TableColumn.TITLE,
  accessorFn: projectResponse => ({
    buyerOriginalName: projectResponse.buyer?.originalName,
    logoURL: projectResponse.buyer?.logoURL,
    tenderTitle: projectResponse.title,
    buyerId: Number(projectResponse.buyer?.id),
  }),

  Cell: ({ cell }: { cell: MRT_Cell<ProjectTableData> }) => {
    const { buyerOriginalName, logoURL, tenderTitle } = cell.getValue<{
      buyerOriginalName: string;
      logoURL: string;
      tenderTitle: string;
      buyerId: number;
    }>();

    return (
      <TenderTitle
        buyerOriginalName={buyerOriginalName}
        logoURL={logoURL}
        tenderTitle={tenderTitle}
        withTooltip
        size="sm"
      />
    );
  },
  minSize: 500,
});

export const responseDeadline: ColumnType = t => ({
  header: t('project.projectTable.cells.responseDeadline.header'),
  id: TableColumn.RESPONSE_DEADLINE,
  accessorFn: projectResponse =>
    formatDateInParisTZ(projectResponse.responseDeadline),
  enableColumnFilter: false,
  ...fixedWidthProps(210),
});

export const action: ColumnType = t => ({
  header: t('project.projectTable.cells.action.header'),
  id: TableColumn.ACTION,
  Cell: ({ cell }: { cell: MRT_Cell<ProjectTableData> }) => {
    const navigate = useNavigate();
    const onRowClick = () => {
      navigate(`/response/projects/${cell.row.original.id}`);
    };

    return (
      <Button variant="light" onClick={onRowClick}>
        {t('project.projectTable.cells.action.cta')}
      </Button>
    );
  },
  ...fixedWidthProps(110),
});
